/**
 * ARManual - Interactive user manual for the Architectural Request module.
 * Located in System Preferences > Architectural Manager > Manual tab.
 */
import React, { useState } from "react";
import {
  Box, Typography, Paper, Grid, Chip, Alert, List, ListItem,
  ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ArchitectureIcon  from "@mui/icons-material/Architecture";
import PeopleIcon        from "@mui/icons-material/People";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import ArticleIcon       from "@mui/icons-material/Article";
import SettingsIcon      from "@mui/icons-material/Settings";
import HistoryIcon       from "@mui/icons-material/History";
import InfoIcon          from "@mui/icons-material/Info";
import PaletteIcon       from "@mui/icons-material/Palette";
import TaskAltIcon       from "@mui/icons-material/TaskAlt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import GavelIcon         from "@mui/icons-material/Gavel";
import AccessTimeIcon    from "@mui/icons-material/AccessTime";

const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:3, mb:2 };
const bodyTextSx = { color:"#374151", lineHeight:1.8 };
const Badge = ({ label, color="#2f63ad" }) => (
  <Chip size="small" label={label} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", mr:0.5, mb:0.5, bgcolor:`${color}18`, color }} />
);

// ── Sections ──────────────────────────────────────────────────────────────────

const OverviewSection = () => (
  <Box>
    <Alert severity="info" icon={<ArchitectureIcon />} sx={{ mb:2.5, borderRadius:2 }}>
      The Architectural Request module manages homeowner applications for exterior modifications.
      Requests flow through a review and approval process with full documentation, compliance tracking, and billing.
    </Alert>
    <Grid container spacing={2}>
      {[
        { icon:<ArchitectureIcon sx={{ color:"#2f63ad" }} />,   title:"Submit & Track",    desc:"Homeowners submit requests for exterior changes (fences, additions, paint colors). Staff tracks from submission through approval." },
        { icon:<PaletteIcon sx={{ color:"#7c3aed" }} />,        title:"Color Management",  desc:"Enforce approved color palettes and schemes per HOA guidelines. Requests include color selections tracked per type." },
        { icon:<TaskAltIcon sx={{ color:"#16a34a" }} />,        title:"Conditions",        desc:"Set approval conditions that must be met before work begins. Track each condition as met or waived." },
        { icon:<CompareArrowsIcon sx={{ color:"#d97706" }} />,  title:"Before/After",      desc:"Upload before and after photos to document that work was completed per the approved plans." },
        { icon:<ArticleIcon sx={{ color:"#0369a1" }} />,        title:"Letters",           desc:"Generate and send approval/denial letters from templates. All letters are logged per request." },
        { icon:<ReceiptLongIcon sx={{ color:"#dc2626" }} />,    title:"Fees",              desc:"Track application fees and generate billing records tied to the AR type and billing rules." },
      ].map(({ icon, title, desc }) => (
        <Grid item xs={12} sm={6} key={title}>
          <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", p:2, height:"100%" }}>
            <Box sx={{ display:"flex", gap:1.5, alignItems:"flex-start" }}>
              <Box sx={{ mt:0.25 }}>{icon}</Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
                <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const SubmittingSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Architectural Requests can be created by staff from the AR list or member profile page.
      Homeowners can also submit via the member portal if enabled.
    </Typography>
    {[
      { step:"1", title:"Select the property", desc:"Search by address or owner. The homeowner's contact info populates automatically." },
      { step:"2", title:"Choose the request type", desc:"Select what type of work is being requested (e.g. Fence, Addition, Paint, Landscaping). The type controls which subtypes, color options, and required documents appear." },
      { step:"3", title:"Select subtype (if enabled)", desc:"Choose the specific subtype. If multi-type selection is enabled, multiple types/subtypes can be selected on one request." },
      { step:"4", title:"Color selection", desc:"If the request type includes a color scheme, the homeowner selects from pre-approved colors. A freeform override can be configured per subtype." },
      { step:"5", title:"Upload supporting documents", desc:"Required documents (site plan, contractor info, HOA application form) are listed automatically based on type. Upload them before submitting." },
      { step:"6", title:"Contractor details", desc:"Add the licensed contractor's name, license number, and contact information." },
      { step:"7", title:"Submit", desc:"The request is created and the staff reviewer receives an assignment notification. Completion dates and anticipated start dates are set here." },
    ].map(({ step, title, desc }) => (
      <Box key={step} sx={{ display:"flex", gap:2, mb:1.5 }}>
        <Box sx={{ minWidth:28, height:28, borderRadius:"50%", bgcolor:"rgba(47,99,173,0.12)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Typography variant="caption" sx={{ fontWeight:800, color:"#2f63ad" }}>{step}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>{desc}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const StatusSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Typical AR status workflow:
    </Typography>
    <Box sx={{ mb:2 }}>
      <Badge label="Submitted" color="#374151" />
      <Badge label="Under Review" color="#7c3aed" />
      <Badge label="Pending Info" color="#b45309" />
      <Badge label="Board Review" color="#0369a1" />
      <Badge label="Approved" color="#16a34a" />
      <Badge label="Denied" color="#dc2626" />
      <Badge label="Completed" color="#2f63ad" />
    </Box>
    <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>
      Status transitions can be restricted using the Status Workflow tab in System Preferences.
      For example, you can enforce that only "Under Review" can transition to "Board Review."
    </Alert>
    <Typography variant="body2" sx={{ ...bodyTextSx }}>
      Each status change is logged in the History tab with timestamp, who changed it, and any notes.
      Email notifications can be configured to send automatically per status transition.
    </Typography>
  </Box>
);

const ConditionsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Approval conditions are requirements that must be satisfied before or during the project.
      They are managed in the Conditions tab of the request.
    </Typography>
    {[
      { title:"Adding conditions",     desc:"Click 'Add Condition' in the Conditions tab. Enter the condition text and any notes. Conditions can have a due date." },
      { title:"Marking as met",        desc:"When the homeowner satisfies a condition, click 'Mark Met'. The date and reviewer are recorded." },
      { title:"Waiving a condition",   desc:"If the board decides to waive a requirement, mark it as 'Waived' with a reason." },
      { title:"Required documents",    desc:"Required documents (configured per type in System Preferences) appear as conditions automatically when the request is created." },
    ].map(({ title, desc }) => (
      <Paper key={title} elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5, mb:1 }}>
        <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
      </Paper>
    ))}
  </Box>
);

const ColorsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      The color management system enforces HOA-approved paint colors and exterior finishes.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Setup (System Preferences)</Typography>
    {[
      "Create Color Schemes (e.g. 'Exterior Paint 2024', 'Roof Materials')",
      "Add individual colors to each scheme (name, hex code, manufacturer reference)",
      "Link schemes to specific AR types - only those colors appear when that type is selected",
      "Per subtype, you can allow 'freeform' color entry if the scheme doesn't apply",
    ].map(t => (
      <Box key={t} sx={{ display:"flex", gap:1, mb:0.75 }}>
        <CheckCircleIcon fontSize="small" sx={{ color:"#7c3aed", mt:"2px", flexShrink:0 }} />
        <Typography variant="body2" sx={{ color:"#374151" }}>{t}</Typography>
      </Box>
    ))}
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mt:2, mb:1 }}>On the Request</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c" }}>
      When a homeowner submits a paint request, they choose from the approved scheme colors.
      Their selection is saved and visible in the Details tab. Multiple colors can be selected
      (e.g. body color, trim color, door color).
    </Typography>
  </Box>
);

const LettersSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Letters (approval letters, denial letters, courtesy notices) are generated from the Arch Letters tab.
    </Typography>
    {[
      { title:"Choose a template", desc:"Select from configured letter templates. Templates are managed in System Preferences > AR Manager > Advanced > Letter Templates." },
      { title:"Preview and customize", desc:"The letter populates with the homeowner's name, property address, request type, conditions, and decision automatically." },
      { title:"Send or export", desc:"Click 'Send via Email' to deliver to the homeowner's email on record, or export as PDF for mailing." },
      { title:"Letter log", desc:"All letters sent are logged - date, time, who sent it, and which template was used." },
    ].map(({ title, desc }) => (
      <Paper key={title} elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5, mb:1 }}>
        <Typography variant="body2" sx={{ fontWeight:700, color:"#2f63ad" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
      </Paper>
    ))}
  </Box>
);

const CompletionSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      After approved work is completed, staff can verify compliance using the Completion Photos tab and Inspector Clock In/Out.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Completion Photos</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Upload Before, After, and Inspection photos to create a visual record proving the work matches the approved plans.
      Photos are organized by category and displayed in a gallery with full-size zoom.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Inspector Clock In/Out</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Inspectors can clock in when arriving on site for a review and clock out when done.
      The system records the exact time on site for each visit, which is useful for billing and compliance documentation.
    </Typography>
    <Alert severity="info" sx={{ borderRadius:2 }}>
      Once the completion photos are uploaded and all conditions are marked as met, change the status to "Completed"
      to close the request lifecycle.
    </Alert>
  </Box>
);

const MultiTypeSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Multi-type selection allows a single AR to cover multiple project types and subtypes simultaneously.
      This is useful when a homeowner is doing combined work (e.g. new fence + paint + landscaping).
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Enable Multi-Type</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Go to System Preferences &rarr; Architectural Manager &rarr; Form Builder and enable "Multi-Type Selection."
      When enabled, the submission form shows checkboxes instead of a single dropdown.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Viewing Multi-Type Requests</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c" }}>
      In the request detail page, all selected types appear in separate cards in the Details tab.
      Each type/subtype combination is shown with its own card, and admins can edit the selection
      if they have the appropriate permission.
    </Typography>
  </Box>
);

const AdminSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      All AR settings are in System Preferences &rarr; Architectural Manager.
    </Typography>
    <Grid container spacing={1.5}>
      {[
        { tab:"Types & Subtypes",  desc:"Create AR types (Fence, Addition, Paint) and subtypes. Per subtype, enable freeform color entry and required documents." },
        { tab:"Color Management",  desc:"Manage color schemes and individual colors. Link schemes to AR types to enforce approved palettes." },
        { tab:"Acknowledgments",   desc:"Configure statements homeowners must acknowledge before submitting (e.g. 'I agree to complete within 6 months')." },
        { tab:"Info Sections",     desc:"Add informational text boxes that appear at the top of the submission form. Supports colored callout boxes and bullet lists." },
        { tab:"Billing Setup",     desc:"Set application fees per AR type and configure auto-billing rules." },
        { tab:"Statuses",          desc:"Add, rename, reorder statuses. Existing requests retain their current status." },
        { tab:"Form Builder",      desc:"Show/hide fields on the submission form, rename labels, and enable multi-type selection." },
        { tab:"Status Workflow",   desc:"Restrict which status transitions are allowed (e.g. can't go from Submitted directly to Completed)." },
        { tab:"Advanced",          desc:"SLA targets per type, request templates, letter templates, webhooks, and required documents per type/subtype." },
        { tab:"Manual",            desc:"This page - reference guide for staff and administrators." },
      ].map(({ tab, desc }) => (
        <Grid item xs={12} sm={6} key={tab}>
          <Paper elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#2f63ad" }}>{tab}</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25, fontSize:12 }}>{desc}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const PermissionsSection = () => (
  <Box>
    <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:"rgba(47,99,173,0.04)" }}>
            {["Feature","Required Permission"].map(h => (
              <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:12, fontWeight:700, color:"#223142", borderBottom:"1px solid rgba(47,99,173,0.08)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            ["View Requests",          "view_architectural_request_details"],
            ["View Letters",           "view_architectural_request_letters"],
            ["View Calendar",          "view_architectural_request_calendar"],
            ["View Comments",          "view_architectural_request_comments"],
            ["View Visits",            "view_architectural_request_visits"],
            ["View Fees",              "view_architectural_request_fees"],
            ["Manage Conditions",      "edit_architectural_request_type"],
            ["Generate Billing",       "manage_architectural_billing"],
            ["Manage Letter Templates","Admin or System Admin role"],
            ["System Preferences",     "Admin or System Admin role"],
          ].map(([feat, perm]) => (
            <tr key={feat} style={{ borderBottom:"1px solid rgba(47,99,173,0.05)" }}>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#223142", fontWeight:600 }}>{feat}</td>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#6b778c", fontFamily:"monospace" }}>{perm}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Paper>
  </Box>
);

const FAQSection = () => {
  const items = [
    {
      q:"Can a homeowner submit their own architectural request?",
      a:"Yes, if the member portal is enabled and the homeowner has portal access. They can submit the form online and track the status. Staff still review and approve/deny the request.",
    },
    {
      q:"How do I enforce that certain documents must be submitted?",
      a:"Go to System Preferences > Architectural Manager > Advanced > Required Documents. Add documents per AR type/subtype. They appear as conditions on the request automatically when submitted.",
    },
    {
      q:"The color picker isn't showing on the submission form.",
      a:"Colors only appear for types that have color schemes linked. Go to System Preferences > Color Management and make sure a scheme is associated with the AR type. Also confirm the type is not set to freeform-only.",
    },
    {
      q:"Can I prevent the board from skipping the review step?",
      a:"Yes - use the Status Workflow tab to restrict which transitions are allowed. For example, block the 'Approved' status unless the current status is 'Board Review'.",
    },
    {
      q:"How do I handle a request where the homeowner changed the scope mid-project?",
      a:"Create a new architectural request for the additional work. Link it to the original using the Links feature, or add an internal comment noting the relationship. Each scope of work should have its own approval record for compliance.",
    },
    {
      q:"Why is the Fees tab empty?",
      a:"Fees only appear after a billing record is generated. Click 'Generate Billing' in the header (requires manage_architectural_billing permission). If no rule matches the AR type, a manual entry dialog appears.",
    },
  ];
  return (
    <Box>
      {items.map(({ q, a }) => (
        <Accordion key={q} elevation={0}
          sx={{ border:"1px solid rgba(47,99,173,0.08)", borderRadius:"10px !important", mb:1, "&:before":{ display:"none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color:"#374151" }}>{a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────
const SECTIONS = [
  { label:"Overview",          icon:<ArchitectureIcon fontSize="small" />,    component:<OverviewSection /> },
  { label:"Submitting",        icon:<GavelIcon fontSize="small" />,           component:<SubmittingSection /> },
  { label:"Statuses",          icon:<HistoryIcon fontSize="small" />,         component:<StatusSection /> },
  { label:"Conditions",        icon:<TaskAltIcon fontSize="small" />,         component:<ConditionsSection /> },
  { label:"Color Management",  icon:<PaletteIcon fontSize="small" />,         component:<ColorsSection /> },
  { label:"Letters",           icon:<ArticleIcon fontSize="small" />,         component:<LettersSection /> },
  { label:"Completion",        icon:<CompareArrowsIcon fontSize="small" />,   component:<CompletionSection /> },
  { label:"Multi-Type",        icon:<ArchitectureIcon fontSize="small" />,    component:<MultiTypeSection /> },
  { label:"Admin Settings",    icon:<SettingsIcon fontSize="small" />,        component:<AdminSection /> },
  { label:"Permissions",       icon:<InfoIcon fontSize="small" />,            component:<PermissionsSection /> },
  { label:"FAQ",               icon:<InfoIcon fontSize="small" />,            component:<FAQSection /> },
];

const ARManual = () => {
  const [selected, setSelected] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>Architectural Requests - User Manual</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>
          Reference guide for reviewers, administrators, and board members.
        </Typography>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden", position:"sticky", top:16 }}>
            {SECTIONS.map(({ label, icon }, i) => (
              <Box key={label} onClick={() => setSelected(i)}
                sx={{ display:"flex", alignItems:"center", gap:1.5, px:2, py:1.25, cursor:"pointer",
                  bgcolor: selected===i ? "rgba(47,99,173,0.08)" : "transparent",
                  borderLeft: selected===i ? "3px solid #2f63ad" : "3px solid transparent",
                  "&:hover":{ bgcolor:"rgba(47,99,173,0.04)" }, transition:"all 0.15s" }}>
                <Box sx={{ color: selected===i ? "#2f63ad" : "#6b778c" }}>{icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: selected===i ? 700 : 500, color: selected===i ? "#2f63ad" : "#374151", fontSize:13 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{ ...cardSx, mb:0 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2.5, pb:2, borderBottom:"1px solid rgba(47,99,173,0.08)" }}>
              <Box sx={{ color:"#2f63ad" }}>{SECTIONS[selected].icon}</Box>
              <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>{SECTIONS[selected].label}</Typography>
            </Box>
            {SECTIONS[selected].component}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ARManual;
